import React, { useState } from 'react';
import { Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useTheme } from '@mui/material';
import { DatabaseTable } from '../../../../shared/components/ui/DatabaseTable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Participant } from '../../../../shared/services/visitors/visitorService';
import { VisitorFilters } from './VisitorFilters';
import { formatBRL } from '../../../../utils/formatBRL';
import { visitorService } from '../../../../shared/services/visitors/visitorService';
import { useToast } from '../../../../shared/hooks/useToast';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useQueryClient } from '@tanstack/react-query';

interface VisitorTableProps {
  participants: Participant[];
  isLoading: boolean;
  onView: (participant: Participant) => void;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
  eventId: string; 
  labelInteiro?: string;
  labelMeia?: string;
  labelEstudante?: string;
  labelCrianca?: string;
}

export function VisitorTable({
  participants,
  isLoading,
  onView,
  page,
  limit,
  total,
  onPageChange,
  onPageSizeChange,
  eventId,
  labelInteiro,
  labelMeia,
  labelEstudante,
  labelCrianca,
}: VisitorTableProps) {
  const theme = useTheme();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; participant: Participant | null }>({
    open: false,
    participant: null,
  });
  const [isMarking, setIsMarking] = useState(false);

  const getTicketLabel = (type: string) => {
    switch (type) {
      case 'inteiro': return labelInteiro || 'Inteiro';
      case 'meia': return labelMeia || 'Meia';
      case 'estudante': return labelEstudante || 'Estudante';
      case 'crianca': return labelCrianca || 'Criança';
      default: return type;
    }
  };
  const getPaymentStatusLabel = (status: 'open' | 'confirmed' | 'paid' | 'refunded' | 'desistente') => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'confirmed': return 'Confirmado';
      case 'open': return 'Em aberto';
      case 'refunded': return 'Estornado';
      case 'desistente': return 'Desistente';
      default: return status;
    }
  };

  const getPaymentStatusColor = (status: 'open' | 'confirmed' | 'paid' | 'refunded' | 'desistente') => {
    switch (status) {
      case 'paid': return 'success';
      case 'confirmed': return 'info';
      case 'open': return 'warning';
      case 'refunded': return 'error';
      case 'desistente': return 'default';
      default: return 'default';
    }
  };

  const handleMarkAsDesistente = async () => {
    if (!confirmDialog.participant) return;

    try {
      setIsMarking(true);
      await visitorService.markAsDesistente(confirmDialog.participant.id);
      showToast('Participante marcado como desistente', 'success');
      setConfirmDialog({ open: false, participant: null });
      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || 'Erro ao marcar como desistente',
        'error'
      );
    } finally {
      setIsMarking(false);
    }
  };

  const renderCustomActions = (row: Participant) => {
    const participant = row as Participant;
    const canMarkAsDesistente = participant.payment_status === 'open' || participant.payment_status === 'confirmed' || participant.payment_status === 'paid';

    return (
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', width: '100%' }}>
        <Tooltip title="Visualizar">
          <IconButton size="small" color="primary" onClick={() => onView(participant)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canMarkAsDesistente && (
          <Tooltip title="Marcar como desistente">
            <IconButton
              size="small"
              color="warning"
              onClick={() => setConfirmDialog({ open: true, participant })}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1, 
      minWidth: 180,
      renderCell: (params: any) => (
        <Typography 
          fontSize={14} 
          color={theme.palette.text.primary}
          sx={{
            opacity: params.row.payment_status === 'desistente' ? 0.6 : 1,
            textDecoration: params.row.payment_status === 'desistente' ? 'line-through' : 'none',
          }}
        >
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'cpf', 
      headerName: 'CPF', 
      width: 120,
      renderCell: (params: any) => (
        <Typography 
          sx={{ 
            fontFamily: 'monospace', 
            fontSize: 12,
            backgroundColor: '#F3F4F6',
            color: '#111827',
            px: 1,
            py: 0.5,
            borderRadius: 1
          }}
        >
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'email', 
      headerName: 'E-mail', 
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => (
        <Typography fontSize={14} color={theme.palette.text.primary}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'ticket_type', 
      headerName: 'Ingresso', 
      width: 120,
      renderCell: (params: any) => (
        <Typography fontSize={14} color={theme.palette.text.primary}>
          {getTicketLabel(params.value)}
        </Typography>
      )
    },
    {
      field: 'ticket_value',
      headerName: 'Valor',
      width: 130,
      renderCell: (params: any) => {
        const value = Number(params.value);
        const hasDiscount = params.row.discount_applied && Number(params.row.discount_applied) > 0;
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography fontSize={14} color={theme.palette.text.primary}>
              {formatBRL(value)}
            </Typography>
            {hasDiscount && (
              <Chip
                label={`-${formatBRL(Number(params.row.discount_applied))}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: 10, height: 18, mt: 0.25 }}
              />
            )}
          </div>
        );
      }
    },
    { 
      field: 'payment_status', 
      headerName: 'Status (PA)', 
      width: 150,
      renderCell: (params: any) => (
        <Chip
          label={getPaymentStatusLabel(params.value)}
          color={getPaymentStatusColor(params.value) as any}
          size="small"
          variant="filled"
        />
      )
    },
    { 
      field: 'created_at', 
      headerName: 'Data de cadastro', 
      width: 120,
      renderCell: (params: any) => (
        <Typography fontSize={14} color={theme.palette.text.primary}>
          {params.value ? format(new Date(params.value), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}
        </Typography>
      )
    },
  ];

  return (
    <>
      {/* Só mostra o filtro quando há um evento selecionado */}
      {eventId && <VisitorFilters />}
      <DatabaseTable
        rows={participants as any}
        columns={columns}
        loading={isLoading}
        onView={onView as any}
        hideDelete
        hideEdit
        hideView
        renderCustomActions={renderCustomActions}
        pageSizeOptions={[5, 15, 25, 50, 100]}
        paginationMode="server"
        page={page}
        pageSize={limit}
        rowCount={total}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        title="Lista de Participantes"
        subtitle="Gerencie os participantes do evento"
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, participant: null })}
      >
        <DialogTitle>Marcar como Desistente</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja marcar o participante <strong>{confirmDialog.participant?.name}</strong> como desistente?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação só pode ser aplicada a participantes com pagamento em aberto. 
            O participante continuará aparecendo na listagem, mas será excluído dos exports.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, participant: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleMarkAsDesistente}
            variant="contained"
            color="warning"
            disabled={isMarking}
          >
            {isMarking ? 'Marcando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}